import { all, call, put, select, takeLatest } from "redux-saga/effects";
import {
  FACTORY_RESTORE,
  FORCE_SETTINGS,
  REBOOT,
  REQUEST_FIRMWARE,
  REQUEST_LOCATION,
  SAVE_DEVICES,
  SELECT_DEVICE,
  SEND_COMMAND,
  SEND_MESSAGE,
  SEND_SERVER_CONFIG,
  SEND_SMS,
  SHUT_DOWN,
  WS_DATA_DEVICE,
} from "../constants/device.constants";
import { DeviceAPI } from "../../utils/API/DeviceAPI";
import { selectSelectedDevice } from "../selectors/device.selector";
import { reselectDevice, v4DeviceSaved } from "../actions/device.actions";
import { selectBeacon } from "../actions/beacon.actions";
import { selectBeacons } from "../selectors/beacon.selector";
import { selectCurrentUser } from "../selectors/app.selectors";
import { APP_MOUNTED, USER_CHANGED } from "../constants/app.constants";
import Device from "../../utils/models/Device";
import v4api, {
  v4ScopeFor,
  setDeviceProperty,
  deleteDeviceProperty,
} from "../../api/V4client";
import { DEVICES } from "../constants/device.constants";
import { diffV4ExtraFields } from "../../utils/models/v4DeviceProperties";
import { ADMIN_PAGE } from "../../containers/AdminPage/constants";
import { deviceChanged } from "../../containers/AdminPage/actions";
import { clearV4DeviceCache } from "../../containers/AdminPage/saga";

const api = new DeviceAPI();

// Plain array until a fetch replaces it with a List — normalise both.
function* selectAdminDevices() {
  const raw = yield select((state) => state[ADMIN_PAGE]?.get?.("devices"));
  if (raw?.toArray) return raw.toArray();
  return Array.isArray(raw) ? raw : [];
}

function* saveV4DeviceProperties(uuid, oldDevice, newDevice, scope) {
  const { upserts, deletes } = diffV4ExtraFields(
    oldDevice?.get?.("extraFields") || [],
    newDevice.get("extraFields") || []
  );
  if (!upserts.length && !deletes.length) return;
  yield all([
    ...upserts.map((p) =>
      call(() => setDeviceProperty(uuid, p.name, p.value, scope))
    ),
    ...deletes.map((p) =>
      call(() => deleteDeviceProperty(uuid, p.name, scope))
    ),
  ]);
}

function* saveDevices({ devices }) {
  const v4Devices = devices.filter((d) => d.isFromV4);
  const v3Devices = devices.filter((d) => !d.isFromV4);

  if (v3Devices.length) {
    yield all(v3Devices.map((d) => api.saveDevice(d)));
  }

  for (const d of v4Devices) {
    const uuid = d.get('id');
    // `id` only: a merged V3 row can share the uuid but has V3-shaped fields.
    const matches = (dev) => dev?.get?.('id') === uuid;
    // Same order selectDevices renders from; a V4-native session seeds `data`.
    // With no previous record the diff rewrites every property and, since
    // `deletes` comes only from the old fields, never deletes anything.
    const deviceData = yield select((state) => state[DEVICES]?.get('data'));
    const currentList = yield select((state) => state[DEVICES]?.get('v4OnlyDevices'));
    const adminDevices = yield* selectAdminDevices();
    const oldDevice =
      deviceData?.find?.(matches) ||
      currentList?.find?.(matches) ||
      adminDevices.find(matches);
    const accountId =
      d.get('accountId') ||
      +d.get('v4Metadata')?.['v3-account-id'] ||
      oldDevice?.get?.('accountId');
    const scope = accountId ? yield call(() => v4ScopeFor(accountId)) : undefined;

    if (!oldDevice || oldDevice.get('name') !== d.get('name')) {
      yield call(() => v4api.devicesPartialUpdate(uuid, uuid, {
        name: d.get('name'),
      }, scope));
    }

    yield call(saveV4DeviceProperties, uuid, oldDevice, d, scope);

    // New `device` prop identity — this is what stops the card spinning.
    yield put(v4DeviceSaved(d));
    // Admin keeps its own copy; drop the cache or it republishes the stale row.
    if (adminDevices.some(matches)) {
      clearV4DeviceCache();
      yield put(deviceChanged(d));
    }
  }
}

function* sendMessage({ message, devices, onResult }) {
  // devices may arrive as an Immutable.List (from selectors) or a plain array
  // (from alarm details). Normalize so `.length` works in both cases.
  const v3Devices = Array.from(devices || []).filter((d) => !d?.isFromV4);
  if (!v3Devices.length) {
    // Always notify the caller so it knows this saga is done — the dialog
    // waits for both V3 and V4 sagas before deciding what to render.
    if (onResult) onResult({ success: true, errors: [] });
    return;
  }
  // Preserve legacy bubble-up behavior when no caller is listening.
  if (!onResult) {
    yield api.sendMessage(message, v3Devices);
    return;
  }
  try {
    yield api.sendMessage(message, v3Devices);
    onResult({ success: true, errors: [] });
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.message || e?.message || 'unknown error';
    onResult({ success: false, errors: [{ status, detail }] });
  }
}

function* sendCommand({ command, device }) {
  yield api.sendCommand(command, device);
}
function* sendSMS({ command, device }) {
  yield api.sendSMS(command, device);
}

function* sendServerConfig({ device, config }) {
  if (config?.toJS) config = config.toJS();
  if (device.toJS) device = device.toJS();
  yield api.sendServerConfig({
    ...(config || device),
    id: device.id,
  });
}

function* requestLocation({ device }) {
  yield api.requestLocation(device);
}

function* requestFirmware({ device }) {
  yield api.requestFirmware(device);
}

function* reboot({ device }) {
  yield api.reboot(device);
}

function* _reselectDevice() {
  const selectedDevice = yield select(selectSelectedDevice());
  yield put(reselectDevice(selectedDevice));
}

function* getStatus() {
  const device = yield select(selectSelectedDevice());
  if (device?.isFromV4) return;
  const selectedDeviceId = localStorage.getItem("selectedDeviceId");
  if (device && +device.id !== +selectedDeviceId) {
    localStorage.setItem("selectedDeviceId", device.id);
    yield api.getStatus(device);
  } else {
    localStorage.removeItem("selectedDeviceId");
  }
}

function* selectBeaconForDevice() {
  const device = yield select(selectSelectedDevice());
  if (device && device.get("beaconIds") && device.get("beaconIds").length) {
    const beacons = yield select(selectBeacons());
    const beacon = beacons.find((b) => +b.id === +device.get("beaconIds")[0]);
    yield put(selectBeacon(beacon));
  }
}

function* forceSettings({ device, onlyFailed }) {
  yield api.forceSettings(device, onlyFailed);
}
function* shutDown({ device }) {
  yield api.shutDown(device.id);
}
function* factoryRestore({ device }) {
  yield api.factoryRestore(device.id);
}
function* getDeviceProtocols() {
  if (Device.protocols) return;
  const user = yield select(selectCurrentUser());
  if (!user || user.isV4Native) return;
  Device.protocols = yield api.getDeviceProtocols();
}

export const deviceSagas = [
  takeLatest(APP_MOUNTED, getDeviceProtocols),
  takeLatest(USER_CHANGED, getDeviceProtocols),

  takeLatest(WS_DATA_DEVICE, _reselectDevice),
  takeLatest(SAVE_DEVICES, saveDevices),
  takeLatest(SEND_MESSAGE, sendMessage),
  takeLatest(SEND_COMMAND, sendCommand),
  takeLatest(SEND_SMS, sendSMS),
  takeLatest(SEND_SERVER_CONFIG, sendServerConfig),
  takeLatest(REQUEST_LOCATION, requestLocation),
  takeLatest(REQUEST_FIRMWARE, requestFirmware),
  takeLatest(SELECT_DEVICE, getStatus),
  takeLatest(SELECT_DEVICE, selectBeaconForDevice),
  takeLatest(REBOOT, reboot),
  takeLatest(FORCE_SETTINGS, forceSettings),
  takeLatest(SHUT_DOWN, shutDown),
  takeLatest(FACTORY_RESTORE, factoryRestore),
];
